import React from 'react';
import { Link, useParams, LinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBestLanguage } from '../utils/languageStorage';

/**
 * Normalize i18n language codes to URL-compatible codes
 * Maps i18n codes like 'zh', 'zh-CN' to URL codes like 'cn'
 */
const normalizeLangCode = (langCode: string): string => {
  const normalized = langCode.toLowerCase().split('-')[0];
  switch (normalized) {
    case 'zh':
      return 'cn';
    default:
      return normalized;
  }
};

/**
 * LocalizedLink component that automatically prefixes links with the current language
 * Uses resilient language detection with fallback chain:
 * 1. useParams() lang (when inside /:lang/* route context)
 * 2. i18n.language (current i18n language)
 * 3. getBestLanguage() (from language storage)
 * 4. 'en' (final fallback)
 * 
 * Usage: Replace <Link to="/about">About</Link> with <LocalizedLink to="/about">About</LocalizedLink>
 */
export const LocalizedLink: React.FC<LinkProps> = ({ to, ...props }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  
  // Resilient language detection with priority fallback chain
  const getCurrentLanguage = (): string => {
    // 1. Try useParams() first (works when inside /:lang/* route context)
    if (lang) {
      return lang;
    }
    
    // 2. Try i18n.language (works everywhere, synced by LanguageWrapper)
    if (i18n.language) {
      return normalizeLangCode(i18n.language);
    }
    
    // 3. Try getBestLanguage() from storage (fallback for edge cases)
    try {
      return getBestLanguage();
    } catch (error) {
      console.warn('Failed to get best language from storage:', error);
    }
    
    // 4. Final fallback
    return 'en';
  };
  
  const currentLang = getCurrentLanguage();
  
  // Handle both string and object 'to' prop
  const localizedTo = typeof to === 'string' 
    ? `/${currentLang}${to.startsWith('/') ? to : `/${to}`}`
    : {
        ...to,
        pathname: `/${currentLang}${to.pathname?.startsWith('/') ? to.pathname : `/${to.pathname || ''}`}`
      };

  return <Link to={localizedTo} {...props} />;
};

export default LocalizedLink;